<script lang="ts">
	import { getPostTradeDetails } from '$lib/data/userInteract';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import { LimitStatus, type minLimitOrderRange } from '$lib/types/userInteract';
	import { debounce, getNumBackInUnits, sanitizeInputs, toBN } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import {
		currentMarket,
		currentMarketAsset,
		currentMarketIdentifier,
		postTradeDetails
	} from '../store';
	import {
		executionType,
		limitInputForTrade,
		limitOrderTriggerStatus,
		margin,
		marginInputForTrade,
		triggerInputForTrade,
		longOrShort
	} from './store';
	import { ethers } from 'ethers';
	import { fly } from 'svelte/transition';
	import { LARGE_POSITIVE_INT } from '$lib/web3/contants';
	import { marketsConfig } from '$lib/config/markets';
	import { onDestroy } from 'svelte';

	export let isLong: boolean;

	let limitPrice: string;
	let triggerPrice: string;
	let priceOfAsset: string;

	$: priceOfAsset = $currentMarket?.safePrice || '0';

	function onTriggerChange() {
		triggerPrice = sanitizeInputs(triggerPrice);
		if (triggerPrice) triggerInputForTrade.set(toBN(triggerPrice).toString());
		else triggerInputForTrade.set('0');
		checkValidPrice();
	}

	const checkValidPrice = () => {
		if (!$currentMarket) return;
		if (isLong) {
			if (new BigNumber($triggerInputForTrade).lt(new BigNumber(priceOfAsset))) {
				limitOrderTriggerStatus.set(LimitStatus.NotValidOrder);
				return;
			}
		} else {
			if (new BigNumber($triggerInputForTrade).gt(new BigNumber(priceOfAsset))) {
				limitOrderTriggerStatus.set(LimitStatus.NotValidOrder);
				return;
			}
		}
		limitOrderTriggerStatus.set(LimitStatus.Ok);
	};

	const currentMarketEventListener = currentMarket.subscribe(() => {
		checkValidPrice();
	});

	const setDefaultValues = () => {
		if ($currentMarket && $currentMarket.currentOraclePrice && !limitPrice && !triggerPrice) {
			if (isLong) {
				let triggerAmt = new BigNumber($currentMarket.currentOraclePrice)
					.multipliedBy(1.01)
					.toString();
				triggerPrice = getNumBackInUnits(
					triggerAmt,
					18,
					marketsConfig[$currentMarket.asset].showDecimals
				) as string;
				let limitAmt = LARGE_POSITIVE_INT;
				limitPrice = getNumBackInUnits(
					limitAmt,
					18,
					marketsConfig[$currentMarket.asset].showDecimals
				) as string;
			} else {
				let triggerAmt = new BigNumber($currentMarket.currentOraclePrice)
					.multipliedBy(0.99)
					.toString();
				triggerPrice = getNumBackInUnits(
					triggerAmt,
					18,
					marketsConfig[$currentMarket.asset].showDecimals
				) as string;
				limitPrice = '0';
			}
			limitInputForTrade.set(toBN(limitPrice).toString());
			onTriggerChange();
			checkValidPrice();
		}
	};

	const executionTypeEventListener = executionType.subscribe(() => {
		setDefaultValues();
	});

	const currentMarketAssetEventListener = currentMarketAsset.subscribe(() => {
		limitPrice = '';
		triggerPrice = '';
		setDefaultValues();
	});

	const postTradeDetailsEventListener = postTradeDetails.subscribe(() => {
		checkValidPrice();
	});

	onDestroy(() => {
		currentMarketEventListener();
		executionTypeEventListener();
		currentMarketAssetEventListener();
		postTradeDetailsEventListener();
	});
</script>

<div class="my-6">
	<div
		class="mt-4 flex justify-between items-center w-full border border-fx-grey-500 bg-fx-gradient-8 rounded-lg squircle-lg py-2 px-3"
		class:border-fx-red={$limitOrderTriggerStatus != LimitStatus.Ok}
	>
		<div class="flex items-center gap-1">
			<img
				src="./less_than_icon.svg"
				alt="less than icon"
				class="w-2.5 h-2.5"
				class:-scale-x-100={!isLong}
			/>
			<input
				inputmode="decimal"
				autocomplete="off"
				autocorrect="off"
				pattern="^[0-9]*[.,]?[0-9]*$"
				minlength="1"
				maxlength="79"
				spellcheck="false"
				type="string"
				placeholder="0"
				data-testid="open-position-amount"
				class="bg-transparent text-fx-white font-medium outline-none text-sm "
				bind:value={triggerPrice}
				on:input={onTriggerChange}
				use:debounce={{ triggerPrice, func: getPostTradeDetails, duration: 300 }}
			/>
		</div>

		<div class="text-fx-grey-200 text-xs flex gap-2">
			<Tooltip header="Trigger Price" title="Price at which the order is placed.">
				<div class="text-fx-grey-400 border-r border-fx-grey-400 pr-2 pt-px">Stop Price</div>
			</Tooltip>
			<div class="px-1 py-0.5 rounded-sm cursor-pointer">USD</div>
		</div>
	</div>

	{#if $limitOrderTriggerStatus != LimitStatus.Ok}
		<div
			class="text-fx-red text-xxs mt-1 absolute font-semibold"
			transition:fly={{ y: -10, duration: 200 }}
		>
			{$longOrShort == 'Long'
				? `Min Stop Price: ${getNumBackInUnits(
						new BigNumber(priceOfAsset).toString(),
						18,
						$currentMarket ? marketsConfig[$currentMarket.asset].showDecimals : 4
				  )}`
				: `Max Stop Price: ${getNumBackInUnits(
						new BigNumber(priceOfAsset).toString(),
						18,
						$currentMarket ? marketsConfig[$currentMarket.asset].showDecimals : 4
				  )}`}
		</div>
	{/if}
</div>
